/* © 2017-2026 Booz Allen Hamilton Inc. All Rights Reserved. */

import { lazy, type ComponentType, type ReactElement } from 'react';
import { createBrowserRouter, type RouteObject } from 'react-router';
import { NotFoundPage, RequireAuthExternal } from 'sarsaparilla';

import { MobileSessionHandoff } from 'shared-ui/mobile-session';
import { ExternalAppLayout } from 'shared-ui/external-app-base';
import { ErrorOrNotFoundPage } from 'shared-ui/components/ErrorPage/ErrorOrNotFoundPage';

import CampgroundPageContainer from '../src/camping/containers/campground-page/CampgroundPageContainer';
import { getRoutesArray } from './routesArray';

const PointOfInterestPage = lazy(
    () => import('../src/camping/containers/point-of-interest-page/index')
);
const MobileCreateReservationPage = lazy(
    () => import('../src/camping/containers/mobile-create-reservation-page/index')
);
const OrderDetailsPage = lazy(() => import('../src/shared/containers/OrderDetailsPage'));
const CampsitePage = lazy(
    () => import('../src/camping/containers/campsite-page/CampsitePageContainer')
);

interface CampingRouteConfig {
    path: string;
    componentName: string;
    requireAuth?: boolean;
    children?: CampingRouteConfig[];
}

type RouteComponentMap = Record<string, ComponentType<any>>;

const Components: RouteComponentMap = {
    CampgroundPage: CampgroundPageContainer,
    MobileCreateReservationPage,
    CampsitePage,
    OrderDetailsPage,
    PointOfInterestPage,
};

const withAuthIfRequired = (
    Component: ComponentType<any>,
    requireAuth?: boolean
): ReactElement => {
    if (requireAuth) {
        return (
            <RequireAuthExternal>
                <Component />
            </RequireAuthExternal>
        );
    }

    return <Component />;
};

const toRouteObject = (route: CampingRouteConfig): RouteObject => {
    const Component = Components[route.componentName];

    return {
        path: route.path,
        element: withAuthIfRequired(Component, route.requireAuth),
        children: route.children?.map((childRoute) => {
            const ChildComponent = Components[childRoute.componentName];
            return {
                path: childRoute.path,
                element: withAuthIfRequired(ChildComponent, childRoute.requireAuth),
            };
        }),
    };
};

const routes: RouteObject[] = [
    {
        path: '/',
        element: (
            <ExternalAppLayout globalLoadingUI="progressBar" isGuestAllowed={false} />
        ),
        errorElement: <ErrorOrNotFoundPage />,
        children: [
            {
                errorElement: <ErrorOrNotFoundPage />,
                children: [
                    ...(getRoutesArray() as CampingRouteConfig[]).map(toRouteObject),
                    {
                        path: '/camping/mobile/session',
                        element: <MobileSessionHandoff />,
                    },
                    {
                        path: '*',
                        element: <NotFoundPage />,
                    },
                ],
            },
        ],
    },
];

export const router = createBrowserRouter(routes);
